import { useEffect, useState } from "react";
import { api, type Resume } from "../lib/api";
import { usePageTitle } from "../lib/pageTitle";
import "./ResumePage.scss";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Sec = any;

/** Preserve paragraphs and bullet lines from resume summary text. */
function renderSummary(summary: string) {
  let text = summary.replace(/\r\n/g, "\n").trim();
  // If bullets are inline without newlines, split them out
  if (!text.includes("\n") && /[•]/.test(text)) {
    text = text.replace(/\s*•\s*/g, "\n• ");
  }
  return text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, i) => {
      if (/^[•\-\*]\s*/.test(line)) {
        return (
          <p key={i} className="resume-doc__summary-bullet">
            {line.replace(/^[•\-\*]\s*/, "• ")}
          </p>
        );
      }
      return <p key={i}>{line}</p>;
    });
}

export function ResumePage() {
  const [resume, setResume] = useState<Resume | null>(null);
  const [error, setError] = useState<string | null>(null);

  usePageTitle(
    resume ? `${resume.fullName} — Resume` : error ? "Resume" : "Resume"
  );

  useEffect(() => {
    api
      .resume()
      .then((r) => {
        setResume(r);
      })
      .catch((e) => setError(e.message));
  }, []);

  if (error) {
    return (
      <div className="container resume-page">
        <p className="resume-page__err">{error}</p>
      </div>
    );
  }

  if (!resume) {
    return (
      <div className="container resume-page">
        <p>Loading resume…</p>
      </div>
    );
  }

  const sections = (resume.sections || []) as Sec[];

  return (
    <div className="resume-page page-enter container">
      <div className="resume-page__actions">
        <a className="btn btn--primary" href="/api/v1/resume/pdf">
          Download PDF
        </a>
        <a className="btn btn--ghost" href="/api/v1/resume/html" target="_blank" rel="noreferrer">
          Printable HTML
        </a>
        <button
          className="btn btn--ghost"
          type="button"
          onClick={() => {
            navigator.clipboard?.writeText(window.location.href);
          }}
        >
          Copy share link
        </button>
      </div>

      <article className="resume-doc">
        {resume.htmlContent ? (
          <div dangerouslySetInnerHTML={{ __html: resume.htmlContent }} />
        ) : (
          <>
            <header className="resume-doc__header">
              <h1>{resume.fullName}</h1>
              {resume.title && <p className="resume-doc__title">{resume.title}</p>}
              <p className="resume-doc__contact">
                {[resume.email, resume.phone, resume.location, resume.website]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </header>
            {resume.summary && (
              <div className="resume-doc__summary">{renderSummary(resume.summary)}</div>
            )}
            {sections.map((sec, i) => (
              <section key={i} className="resume-doc__section">
                <h2>{sec.title || sec.type}</h2>
                {sec.type === "skills" &&
                  (sec.items || []).map((g: Sec, j: number) => (
                    <p key={j}>
                      <strong>{g.group}</strong> — {(g.skills || []).join(", ")}
                    </p>
                  ))}
                {sec.type === "links" && (
                  <ul>
                    {(sec.items || []).map((l: Sec, j: number) => (
                      <li key={j}>
                        <a href={l.url} target="_blank" rel="noreferrer">
                          {l.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
                {sec.type !== "skills" &&
                  sec.type !== "links" &&
                  (sec.items || []).map((item: Sec, j: number) => (
                    <div key={j} className="resume-doc__item">
                      <h3>
                        {item.title}
                        {item.organization ? ` · ${item.organization}` : ""}
                      </h3>
                      <div className="resume-doc__meta">
                        {[item.location, [item.startDate, item.endDate].filter(Boolean).join(" – ")]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                      {item.description && <p>{item.description}</p>}
                      {item.highlights && (
                        <ul>
                          {item.highlights.map((h: string, k: number) => (
                            <li key={k}>{h}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
              </section>
            ))}
          </>
        )}
      </article>
    </div>
  );
}
